import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Button,
  Row,
  Col,
  Card,
  Tag,
  Pagination,
  message,
  Spin
} from 'antd';
import { ThemeContext } from '../../context/ThemeContext';
import {
  ArrowUpOutlined,
  CrownOutlined,
  HomeOutlined,
  ApartmentOutlined,
  BankOutlined,
  GlobalOutlined,
  TeamOutlined,
  BuildOutlined,
  DollarOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LandingLayout from './LandingLayoutRedesign';
import ISINCard from '../shared/ISINCard';
import './IssuerCategoryRedesign.css';
import '../LandingPages/LandingHero.css';
import apiClient from '../../services/apiClient';

const { Title, Paragraph } = Typography;

// Helper functions for premium issuer card design
const formatSubOwnership = (val) => {
  if (!val) return '—';
  const clean = val.replace(/_/g, ' ').toUpperCase();
  if (clean === 'GOVERNMENT COMPANY') return 'GOVERNMENT';
  if (clean === 'PSU INSTITUTIONAL OWNED') return 'INSTITUTIONAL';
  if (clean === 'NON PSU BANK') return 'PRIVATE BANK';
  if (clean === 'NBFC PSU') return 'PSU NBFC';
  if (clean === 'NBFC NON PSU') return 'PRIVATE NBFC';
  return clean;
};

const getOwnershipClass = (typeofSubOwnership, typeOfIssuerBasedOnOwnership) => {
  const val = (typeofSubOwnership || typeOfIssuerBasedOnOwnership || '').toUpperCase();
  
  if (
    val.includes('CENTRAL_GOVERNMENT') || 
    val.includes('CENTRAL_GOV') || 
    val === 'GOVERNMENT' || 
    val === 'SOVEREIGN'
  ) {
    return 'ownership-sovereign';
  }
  
  if (
    val.includes('STATE_GOVERNMENT') || 
    val.includes('STATE_GOV')
  ) {
    return 'ownership-state';
  }
  
  if (
    val === 'GOVERNMENT_COMPANY' || 
    val === 'GOVERNMENT COMPANY' ||
    val === 'PSU' || 
    val.includes('PSU_BANK') || 
    val.includes('NBFC_PSU') || 
    val.includes('PSU_NBFC')
  ) {
    return 'ownership-psu';
  }
  
  if (
    val.includes('INST_OWNED') || 
    val.includes('INSTITUTIONAL_OWNED') || 
    val.includes('INST_CONTROLLED') ||
    val.includes('INSTITUTIONAL') ||
    val.includes('FINANCIAL_INSTITUTION')
  ) {
    return 'ownership-institutional';
  }
  
  return 'ownership-private';
};

const getRatingBorderClass = (rating) => {
  if (!rating) return 'rating-border-unrated';
  const clean = rating.toUpperCase().replace(/\s+/g, '');
  if (clean === 'NA' || clean === 'N/A' || clean === '-' || clean === '—' || clean === 'WR' || clean === 'UNRATED' || clean === 'WD') {
    return 'rating-border-unrated';
  }
  if (clean.includes('AAA')) return 'rating-border-aaa';
  if (clean.includes('AA')) return 'rating-border-aa';
  if (clean.includes('A')) return 'rating-border-a';
  if (clean.includes('BBB')) return 'rating-border-bbb';
  return 'rating-border-sub-bbb';
};

const getRatingBadgeClass = (rating) => {
  if (!rating) return 'rating-badge-unrated';
  const clean = rating.toUpperCase().replace(/\s+/g, '');
  if (clean === 'NA' || clean === 'N/A' || clean === '-' || clean === '—' || clean === 'WR' || clean === 'UNRATED' || clean === 'WD') {
    return 'rating-badge-unrated';
  }
  if (clean.includes('AAA')) return 'rating-badge-aaa';
  if (clean.includes('AA')) return 'rating-badge-aa';
  if (clean.includes('A')) return 'rating-badge-a';
  if (clean.includes('BBB')) return 'rating-badge-bbb';
  return 'rating-badge-sub-bbb';
};



const formatMarketCap = (val) => {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  if (num === 0) return '—';
  return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' Cr';
};

const formatBondBorrowings = (val) => {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  if (num === 0) return '—';
  return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' Cr';
};

const getMarketCapValue = (item) => {
  if (item.marketcap && parseFloat(item.marketcap) > 0) {
    return formatMarketCap(item.marketcap);
  }
  return '—';
};

const getBondBorrowingsValue = (item) => {
  if (item.totalBondBorrowings && parseFloat(item.totalBondBorrowings) > 0) {
    return formatBondBorrowings(item.totalBondBorrowings);
  }
  return '—';
};

const cleanRatingValue = (rating) => {
  if (!rating) return 'NA';
  let cleaned = rating.toString().trim();
  cleaned = cleaned.replace(/^\[[A-Za-z]+\]/i, '');
  cleaned = cleaned.replace(/^(CARE|IND|CRISIL|ICRA|PROVISIONAL CARE|FITCH|S&P|SP)\s+/i, '');
  cleaned = cleaned.replace(/\s*[\(\/;\-]\s*(stable|negative|positive|developing|rating|watch|outlook).*$/i, '');
  cleaned = cleaned.replace(/\s*\([^)]*\)/g, '');
  cleaned = cleaned.trim().toUpperCase();
  if (cleaned === '-' || cleaned === '—' || cleaned === 'WR' || cleaned === 'UNRATED' || cleaned === 'WD' || cleaned === 'N/A' || cleaned === 'NA' || !cleaned) {
    return 'NA';
  }
  return cleaned;
};

const IssuerCategorizationRedesign = ({ skipLayout = false }) => {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [issuersData, setIssuersData] = useState([]);
  const [allIssuersData, setAllIssuersData] = useState([]); // Cache for non-ISIN categories
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(16);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'GOVERNMENT_COMPANY');
  const [categoryCounts, setCategoryCounts] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [highlightedMainCategory, setHighlightedMainCategory] = useState(null);

  // Pastel surfaces — Wise-tinted (DESIGN.md light mint / warm neutrals)
  const backgroundColors = [
    '#e2f6d5',
    '#f0f5e8',
    '#e8ebe6',
    '#fff4e6',
    '#eef6e0',
    '#fafbf8',
  ];

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  // Category structure matching the new API
  const categoryStructure = [
    {
      key: 'government',
      label: 'Central Govt.',
      icon: <CrownOutlined />
    },
    {
      key: 'stategovernment',
      label: 'State Govt.',
      icon: <HomeOutlined />
    },
    {
      key: 'psu',
      label: 'PSU Corps',
      icon: <ApartmentOutlined />,
      subcategories: [
        { key: 'GOVERNMENT_COMPANY', label: 'Government Company', icon: <CrownOutlined /> },
        { key: 'CENTRAL_GOVERNMENT', label: 'Central Government', icon: <CrownOutlined /> },
        { key: 'STATE_GOVERNMENT', label: 'State Government', icon: <HomeOutlined /> },
        { key: 'PSU_INSTITUTIONAL_OWNED', label: 'Institutional Owned', icon: <BankOutlined /> }
      ]
    },
    {
      key: 'non-psu',
      label: 'Private Sector Corps',
      icon: <GlobalOutlined />,
      subcategories: [
        { key: 'DOMESTIC', label: 'Domestic', icon: <HomeOutlined /> },
        { key: 'FOREIGN', label: 'Foreign', icon: <GlobalOutlined /> },
        { key: 'WIDELY_HELD', label: 'Widely Held', icon: <TeamOutlined /> },
        { key: 'INSTITUTIONAL_OWNED', label: 'Institutional Owned', icon: <BankOutlined /> }
      ]
    },
    {
      key: 'banks',
      label: 'Banks',
      icon: <BankOutlined />,
      subcategories: [
        { key: 'PSU_BANK', label: 'PSU Banks', icon: <BankOutlined /> },
        { key: 'NON_PSU_BANK', label: 'Private Sector Banks', icon: <BankOutlined /> },
        { key: 'FINANCIAL_INSTITUTION', label: 'Financial Institution', icon: <BankOutlined /> },
        { key: 'SMALL_FINANCE_BANKS', label: 'Small Finance Banks', icon: <BankOutlined /> }
      ]
    },
    {
      key: 'nbfc',
      label: 'NBFC',
      icon: <BuildOutlined />,
      subcategories: [
        { key: 'NBFC_PSU', label: 'PSU NBFC', icon: <BuildOutlined /> },
        { key: 'NBFC_NON_PSU', label: 'Non-PSU NBFC', icon: <BuildOutlined /> }
      ]
    },
    {
      key: 'municipal',
      label: 'Municipal Corps',
      icon: <HomeOutlined />,
      subcategories: [
        { key: 'MUNICIPAL_CORPORATION', label: 'Municipal Corporations', icon: <HomeOutlined /> }
      ]
    },
    {
      key: 'other',
      label: 'Other',
      icon: <BuildOutlined />,
      subcategories: [
        { key: 'OTHER', label: 'TRUSTS', icon: <DollarOutlined /> }
        
      ]
    }
  ];

  // Helper to enrich a list of issuers with detailed data
  const enrichIssuers = async (list) => {
    if (!Array.isArray(list) || list.length === 0) return list;
    return Promise.all(
      list.map(async (item) => {
        let enrichedItem = { ...item };
        
        // 1. Fetch details from master-issuer
        if (item.corporateIdentityNumber) {
          try {
            const detailRes = await apiClient.get(`${baseUrl}/master-issuer/${item.corporateIdentityNumber}`);
            if (detailRes.data && detailRes.data.statusCode === 200 && detailRes.data.data) {
              enrichedItem = { ...enrichedItem, ...detailRes.data.data };
            }
          } catch (err) {
            console.error('Error fetching details for ' + item.corporateIdentityNumber, err);
          }
        }
        
        // 2. Fetch ratings from ratings/issuer
        const issuerName = enrichedItem.issuer || item.issuer;
        if (issuerName) {
          try {
            const ratingsRes = await apiClient.get(`${baseUrl}/ratings/issuer/${encodeURIComponent(issuerName)}`);
            if (ratingsRes.data && ratingsRes.data.statusCode === 200 && ratingsRes.data.data) {
              const ratingsData = ratingsRes.data.data;
              const agencies = ['crisil', 'icra', 'care', 'india', 'fitch', 'sp'];
              let foundRating = null;
              
              for (const agency of agencies) {
                const agencyRatings = ratingsData[agency];
                if (agencyRatings && Array.isArray(agencyRatings) && agencyRatings.length > 0) {
                  const rec = agencyRatings[0];
                  const rVal = rec.rating || (rec.id && rec.id.rating);
                  if (rVal) {
                    foundRating = rVal;
                    break;
                  }
                }
              }
              
              if (foundRating) {
                enrichedItem.rating = cleanRatingValue(foundRating);
              }
            }
          } catch (err) {
            console.error('Error fetching ratings for ' + issuerName, err);
          }
        }
        
        return enrichedItem;
      })
    );
  };

  // Fetch issuers data from the new categorization API
  const fetchIssuersData = async (category = selectedCategory, page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      
      // Check if this is an ISIN category (government or stategovernment)
      const isISINCategory = category === 'government' || category === 'stategovernment';
      const url = isISINCategory
        ? `${baseUrl}/isin-category/category/${category}?page=${page - 1}&size=${size}`
        : `${baseUrl}/master-issuer/categorization/${category}`;
      
      const response = await apiClient.get(url, { validateStatus: () => true });
      if (response.status !== 200) {
        if (response.status === 404 || response.status === 204) {
          setIssuersData([]);
          setTotalItems(0);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = response.data;
      
      if (isISINCategory) {
        // Handle ISIN API response structure (supports pagination)
        if ((result.statusCode === 200 || result.status === 200) && result.data) {
          if (result.data.content && Array.isArray(result.data.content)) {
            setIssuersData(result.data.content);
            setTotalItems(result.data.totalElements || 0);
            setCurrentPage((result.data.pageNumber || 0) + 1);
            setPageSize(result.data.pageSize || size);
          } else if (Array.isArray(result.data)) {
            setIssuersData(result.data);
            setTotalItems(result.data.length);
          } else {
            setIssuersData([]);
            setTotalItems(0);
          }
          
          // Update category count (only on first load)
          if (page === 1) {
            setCategoryCounts(prev => ({
              ...prev,
              [category]: result.data.totalElements || result.data.length || 0
            }));
          }
        } else {
          setIssuersData([]);
          setTotalItems(0);
        }
      } else {
        // Handle issuer categorization API response structure
        // Backend returns all data, so we paginate on frontend
        if ((result.statusCode === 200 || result.status === 200) && result.data) {
          const allIssuers = result.data.content;
          const totalCount = result.data.totalElements || result.data.content.length;
          
          // Cache all data for this category
          setAllIssuersData(allIssuers);
          
          // Paginate on frontend
          const startIndex = (page - 1) * size;
          const endIndex = startIndex + size;
          const paginatedIssuers = allIssuers.slice(startIndex, endIndex);
          
          const enrichedIssuers = await enrichIssuers(paginatedIssuers);
          setIssuersData(enrichedIssuers);
          setTotalItems(totalCount);
          
          // Update category count (only on first load)
          if (page === 1) {
            setCategoryCounts(prev => ({
              ...prev,
              [category]: totalCount
            }));
          }
        } else {
          setIssuersData([]);
          setAllIssuersData([]);
          setTotalItems(0);
        }
      }
    } catch (error) {
      console.error('Error fetching categorization data:', error);
      message.error('Failed to fetch categorization data');
      setIssuersData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts for all issuer-categorization subcategories (single backend call)
  const fetchCategoryCounts = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/master-issuer/categorization/statistics`, {
        validateStatus: () => true,
      });
      if (response.status === 200 && response.data) {
        const result = response.data;
        if ((result.statusCode === 200 || result.status === 200) && result.data) {
          setCategoryCounts(result.data || {});
          return;
        }
      }
      // Fallback: zero everything if API didn't return expected shape
      setCategoryCounts({});
    } catch (error) {
      console.error('Error fetching issuer categorization statistics:', error);
      setCategoryCounts({});
    }
  };

  // Update URL params helper
  const updateURLParams = (params) => {
    const newSearchParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Get the main category for a given subcategory
  const getMainCategoryForSubcategory = (subcategoryKey) => {
    for (const mainCategory of categoryStructure) {
      if (mainCategory.subcategories) {
        for (const subcategory of mainCategory.subcategories) {
          if (subcategory.key === subcategoryKey) {
            return mainCategory.key;
          }
        }
      }
    }
    return null;
  };

  // Check if main category should be highlighted
  const isMainCategoryHighlighted = (mainCategoryKey) => {
    // Highlight if it's the highlighted main category OR if this main category is directly selected OR if a subcategory of this main category is selected
    return highlightedMainCategory === mainCategoryKey || 
           selectedCategory === mainCategoryKey ||
           (selectedCategory !== 'government' && selectedCategory !== 'stategovernment' && getMainCategoryForSubcategory(selectedCategory) === mainCategoryKey);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    setAllIssuersData([]); // Clear cached data when category changes
    
    // Set highlighted main category
    const mainCategoryKey = getMainCategoryForSubcategory(category);
    if (mainCategoryKey) {
      setHighlightedMainCategory(mainCategoryKey);
    } else {
      // If it's a main category itself, set it as highlighted
      const isMainCategory = categoryStructure.some(cat => cat.key === category && !cat.subcategories);
      if (isMainCategory) {
        setHighlightedMainCategory(category);
      } else {
        setHighlightedMainCategory(null);
      }
    }
    
    updateURLParams({ category, page: 1, size: pageSize });
    fetchIssuersData(category, 1, pageSize);
  };

  // Handle page change (for onChange event)
  const handlePageChange = async (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
    updateURLParams({ page, size: pageSize, category: selectedCategory });
    
    // Check if this is an ISIN category (needs API call) or non-ISIN (can use cached data)
    if (selectedCategory === 'government' || selectedCategory === 'stategovernment') {
      // ISIN categories need API call for pagination
      fetchIssuersData(selectedCategory, page, pageSize);
    } else {
      // Non-ISIN categories: paginate from cached data
      if (allIssuersData.length > 0) {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedIssuers = allIssuersData.slice(startIndex, endIndex);
        setLoading(true);
        const enriched = await enrichIssuers(paginatedIssuers);
        setIssuersData(enriched);
        setLoading(false);
      } else {
        // If no cached data, fetch from API
        fetchIssuersData(selectedCategory, page, pageSize);
      }
    }
  };

  // Handle page size change (for onShowSizeChange event)
  const handlePageSizeChange = async (current, size) => {
    setCurrentPage(1); // Reset to first page when page size changes
    setPageSize(size);
    updateURLParams({ page: 1, size, category: selectedCategory });
    
    // Check if this is an ISIN category (needs API call) or non-ISIN (can use cached data)
    if (selectedCategory === 'government' || selectedCategory === 'stategovernment') {
      // ISIN categories need API call for pagination
      fetchIssuersData(selectedCategory, 1, size);
    } else {
      // Non-ISIN categories: paginate from cached data
      if (allIssuersData.length > 0) {
        const startIndex = 0;
        const endIndex = size;
        const paginatedIssuers = allIssuersData.slice(startIndex, endIndex);
        setLoading(true);
        const enriched = await enrichIssuers(paginatedIssuers);
        setIssuersData(enriched);
        setLoading(false);
      } else {
        // If no cached data, fetch from API
        fetchIssuersData(selectedCategory, 1, size);
      }
    }
  };

  // Get selected category label
  const getSelectedCategoryLabel = () => {
    for (const category of categoryStructure) {
      if (category.key === selectedCategory && !category.subcategories) {
        return category.label;
      }
      if (category.subcategories) {
        const subcategory = category.subcategories.find(sub => sub.key === selectedCategory);
        if (subcategory) {
          return `${category.label} - ${subcategory.label}`;
        }
      }
    }
    return 'Issuer Categorization';
  };

  // Get breadcrumb path for current selection (for fintech-style navigation)
  const getBreadcrumbPath = () => {
    const path = [{ label: 'Issuer Categorization', key: null }];
    for (const category of categoryStructure) {
      if (category.key === selectedCategory && !category.subcategories) {
        path.push({ label: category.label, key: category.key });
        return path;
      }
      if (category.subcategories) {
        const subcategory = category.subcategories.find(sub => sub.key === selectedCategory);
        if (subcategory) {
          path.push({ label: category.label, key: category.key });
          path.push({ label: subcategory.label, key: subcategory.key });
          return path;
        }
      }
    }
    return path;
  };

  // Total issuers across all categories (for summary strip)
  const totalIssuersAcrossCategories = () => {
    const keys = categoryStructure.flatMap(cat =>
      cat.subcategories ? cat.subcategories.map(s => s.key) : [cat.key]
    );
    return keys.reduce((sum, key) => sum + (categoryCounts[key] || 0), 0);
  };

  // Handle view details
  const handleViewDetails = (issuer) => {
    const issuerId = issuer.corporateIdentityNumber;
    const isInDashboard = window.location.pathname.includes('/d/');
    if (isInDashboard) {
      navigate(`/d/issuer/details?issuerId=${issuerId}`);
    } else {
      navigate(`/issuer-details/${issuerId}`);
    }
  };

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const menuBar = document.querySelector('.horizontal-menu-bar');
      if (menuBar && !menuBar.contains(event.target)) {
        setExpandedCategories({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch data on component mount and when URL params change
  useEffect(() => {
    const urlCategory = searchParams.get('category') || 'GOVERNMENT_COMPANY';
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const urlSize = parseInt(searchParams.get('size')) || 16;
    
    // Update state if URL params are different
    if (urlCategory !== selectedCategory) {
      setSelectedCategory(urlCategory);
    }
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
    }
    if (urlSize !== pageSize) {
      setPageSize(urlSize);
    }
    
    // Set highlighted main category based on selected category
    const mainCategoryKey = getMainCategoryForSubcategory(urlCategory);
    if (mainCategoryKey) {
      setHighlightedMainCategory(mainCategoryKey);
    } else {
      // If it's a main category itself, set it as highlighted
      const isMainCategory = categoryStructure.some(cat => cat.key === urlCategory && !cat.subcategories);
      if (isMainCategory) {
        setHighlightedMainCategory(urlCategory);
      } else {
        setHighlightedMainCategory(null);
      }
    }
    
    // Fetch data with current params
    fetchIssuersData(urlCategory, urlPage, urlSize);
    fetchCategoryCounts();
  }, []); // Only run on mount

  const formatClassificationValue = (value) => {
    if (!value || value === 'N/A') return 'N/A';
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const content = (
      <div className={`bond-categories-page ${isDark ? 'dark' : ''}`}>
        <div className="container">
          {/* Minimal page header — focus on content */}
          <header className="issuer-categorization-header">
            <h1 className="issuer-categorization-title">
              <BankOutlined className="issuer-categorization-icon" />
              Explore BONDS by Companies
            </h1>
            <p className="issuer-categorization-hint">Browse by ownership and sector</p>
          </header>

        {/* Browse by category */}
        <section className="categories-section" aria-label="Browse by category">
          <div className="horizontal-menu-bar">
            {categoryStructure.map((category) => (
              <div
                key={category.key}
                className={`menu-item menu-item-${category.key} ${isMainCategoryHighlighted(category.key) ? 'active' : ''} ${expandedCategories[category.key] ? 'expanded' : ''}`}
                onClick={() => {
                  if (category.subcategories) {
                    // Toggle dropdown for categories with subcategories
                    setExpandedCategories(prev => {
                      const newState = {};
                      categoryStructure.forEach(cat => {
                        if (cat.key === category.key) {
                          newState[cat.key] = !prev[cat.key];
                        } else {
                          newState[cat.key] = false;
                        }
                      });
                      return newState;
                    });
                  } else {
                    // Directly select categories without subcategories
                    handleCategoryChange(category.key);
                  }
                }}
              >
                <div className="menu-item-icon">{category.icon}</div>
                <div className="menu-item-content">
                  <div className="menu-item-label">
                    {category.label}
                    {categoryCounts[category.key] !== undefined && (
                      <span className="menu-item-count-badge">
                        {categoryCounts[category.key] || 0}
                      </span>
                    )}
                  </div>
                </div>
                {category.subcategories && (
                  <div className="menu-item-dropdown-arrow">▼</div>
                )}
                
                {/* Subcategories Dropdown */}
                {category.subcategories && expandedCategories[category.key] && (
                  <div 
                    className="category-dropdown-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {category.subcategories.map((subcategory) => (
                      <div
                        key={subcategory.key}
                        className={`dropdown-item ${selectedCategory === subcategory.key ? 'active' : ''}`}
                        onClick={() => handleCategoryChange(subcategory.key)}
                      >
                        <div className="dropdown-item-icon">{subcategory.icon}</div>
                        <span className="dropdown-item-label">
                          {subcategory.label}
                        </span>
                        {categoryCounts[subcategory.key] !== undefined && (
                          <span className="dropdown-item-count">
                            {categoryCounts[subcategory.key] || 0}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Results section: Category Info Header Card */}
        <section className="results-section" aria-label="Results">
          <div className="category-info-card-header">
            <div className="category-info-card-header__info">
              {/* Breadcrumb path as tags */}
              <nav className="breadcrumb-nav" aria-label="Breadcrumb">
                {getBreadcrumbPath().map((item, i) => (
                  <span key={item.key || 'root'} className="breadcrumb-item">
                    {i > 0 && <RightOutlined className="breadcrumb-sep" aria-hidden />}
                    <span className={`breadcrumb-pill ${i === getBreadcrumbPath().length - 1 ? 'breadcrumb-current' : ''}`}>
                      {item.label}
                    </span>
                  </span>
                ))}
              </nav>

              {/* Dynamic Category Heading */}
              <h2 className="category-info-card-header__title">
                {getSelectedCategoryLabel()}
              </h2>
            </div>

            {/* Results count badges */}
            <div className="category-info-card-header__badges">
              <span className="summary-badge-category">
                {totalItems > 0 ? `${totalItems} ${totalItems === 1 ? 'Issuer' : 'Issuers'}` : 'No Issuers'}
              </span>
              {totalIssuersAcrossCategories() > 0 && (
                <span className="summary-badge-total">
                  {`${totalIssuersAcrossCategories().toLocaleString('en-IN')} Total`}
                </span>
              )}
            </div>
          </div>
        </section>

        <div className="issuers-grid">
          <Row gutter={[24, 24]}>
            {loading ? (
              // Loading skeleton — same structure as issuer card
              Array.from({ length: 8 }).map((_, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Card className="bond-card loading" hoverable style={{ borderRadius: '16px', backgroundColor: '#f8fafc' }}>
                    <div className="bond-card-inner">
                      <div className="issuer-header-section">
                        <div className="issuer-name-row">
                          <div className="company-logo">
                            <div className="loading-placeholder" style={{ width: '48px', height: '48px', backgroundColor: '#e2e8f0', borderRadius: '10px' }} />
                          </div>
                          <div className="issuer-company-name" style={{ minHeight: '20px' }}>
                            <div className="loading-placeholder" style={{ width: '90%', height: '16px', backgroundColor: '#e2e8f0', borderRadius: '4px' }} />
                          </div>
                        </div>
                        <div className="issuer-meta-row">
                          <div className="loading-placeholder" style={{ width: '100%', height: '14px', backgroundColor: '#e2e8f0', borderRadius: '4px' }} />
                          <div className="loading-placeholder" style={{ width: '85%', height: '14px', backgroundColor: '#e2e8f0', borderRadius: '4px' }} />
                          <div className="loading-placeholder" style={{ width: '75%', height: '14px', backgroundColor: '#e2e8f0', borderRadius: '4px' }} />
                        </div>
                      </div>
                      <div className="issuer-bottom-section">
                        <div className="loading-placeholder" style={{ width: '72px', height: '14px', backgroundColor: '#e2e8f0', borderRadius: '4px' }} />
                      </div>
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              // Real data from API
              issuersData.map((item, index) => {
                // Check if this is a government or stategovernment category (ISIN data)
                if (selectedCategory === 'government' || selectedCategory === 'stategovernment') {
                  // Use ISINCard for government securities
                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <ISINCard 
                        isin={item} 
                        index={index}
                        onViewDetails={handleViewDetails}
                      />
                    </Col>
                  );
                } else {
                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <Card
                        className={`premium-issuer-card ${getRatingBorderClass(item.rating)}`}
                        hoverable
                        onClick={() => handleViewDetails(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="premium-issuer-card__body">
                          {/* Badges row */}
                          <div className="premium-issuer-card__badges-row">
                            <span className={`premium-issuer-card__rating-badge ${getRatingBadgeClass(item.rating)}`}>
                              {cleanRatingValue(item.rating)}
                            </span>
                            <span className={`premium-issuer-card__ownership-badge ${getOwnershipClass(item.typeofSubOwnership, item.typeOfIssuerBasedOnOwnership)}`}>
                              {formatSubOwnership(item.typeofSubOwnership || item.typeOfIssuerBasedOnOwnership)}
                            </span>
                          </div>

                          {/* Company Name */}
                          <h2 className="premium-issuer-card__title">
                            {item.issuer || ''}
                          </h2>

                          <div className="premium-issuer-card__divider" />

                          {/* Metrics Grid */}
                          <div className="premium-issuer-card__metrics-grid">
                            <div className="premium-issuer-card__metric-box">
                              <span className="premium-issuer-card__metric-label">Market Cap</span>
                              <span className="premium-issuer-card__metric-value">
                                {getMarketCapValue(item)}
                              </span>
                            </div>
                            <div className="premium-issuer-card__metric-box">
                              <span className="premium-issuer-card__metric-label">Bond Borrowings</span>
                              <span className="premium-issuer-card__metric-value">
                                {getBondBorrowingsValue(item)}
                              </span>
                            </div>
                          </div>

                          <div className="premium-issuer-card__divider" />

                          {/* Details Grid */}
                          <div className="premium-issuer-card__details-grid">
                            <div className="premium-issuer-card__detail-item">
                              <span className="premium-issuer-card__detail-label">Macro Sector</span>
                              <span className="premium-issuer-card__detail-value--neutral-badge">
                                {formatClassificationValue(item.classification?.macroeconomicSector || item.macroeconomicSector || '—')}
                              </span>
                            </div>
                            <div className="premium-issuer-card__detail-item">
                              <span className="premium-issuer-card__detail-label">Sector</span>
                              <span className="premium-issuer-card__detail-value--neutral-badge">
                                {formatClassificationValue(item.classification?.sector || item.sector || '—')}
                              </span>
                            </div>
                            <div className="premium-issuer-card__detail-item">
                              <span className="premium-issuer-card__detail-label">Industry</span>
                              <span className="premium-issuer-card__detail-value--neutral-badge">
                                {formatClassificationValue(item.classification?.industry || item.industry || '—')}
                              </span>
                            </div>
                            <div className="premium-issuer-card__detail-item">
                              <span className="premium-issuer-card__detail-label">Basic Industry</span>
                              <span className="premium-issuer-card__detail-value--neutral-badge">
                                {formatClassificationValue(item.classification?.basicIndustry || item.basicIndustry || '—')}
                              </span>
                            </div>
                          </div>

                        </div>

                        {/* Footer View Profile */}
                        <div className="premium-issuer-card__footer">
                          <span className="premium-issuer-card__footer-text">View Profile</span>
                          <RightOutlined className="premium-issuer-card__footer-arrow" />
                        </div>
                      </Card>
                    </Col>
                  );
                }
              })
            )}
          </Row>
        </div>

        {/* Pagination */}
        {!loading && totalItems > pageSize && (
          <div className="pagination-container" style={{ marginTop: '32px', marginBottom: '32px', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              current={currentPage}
              total={totalItems}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger
              onShowSizeChange={handlePageSizeChange}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} issuers`}
              pageSizeOptions={['16', '32', '64', '128']}
            />
          </div>
        )}

        {/* Empty State */}
        {!loading && issuersData.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <BankOutlined />
            </div>
            <Title level={3}>No issuers found</Title>
            <Paragraph>
              No issuers found for the selected category
            </Paragraph>
          </div>
        )}
      </div>
    </div>
  );

  return skipLayout ? content : <LandingLayout>{content}</LandingLayout>;
};

export default IssuerCategorizationRedesign;

